import { useEffect, useState } from 'react';
import {
  ChiTietPhieuThanhToanTamUng,
  PhieuThanhToanTamUng,
  PhieuThanhToanTamUngInput,
  PhieuThanhToanTamUngResponse,
  QuyetToanCacLanTamUng,
  ThuePhieuThanhToanTamUng
} from '@/types/schemas/phieu-thanh-toan-tam-ung.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UsePhieuThanhToanTamUngReturn {
  phieuThanhToanTamUngs: PhieuThanhToanTamUng[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addPhieuThanhToanTamUng: (newPhieuThanhToanTamUng: PhieuThanhToanTamUngInput) => Promise<PhieuThanhToanTamUng>;
  updatePhieuThanhToanTamUng: (
    uuid: string,
    updatedPhieuThanhToanTamUng: PhieuThanhToanTamUngInput
  ) => Promise<PhieuThanhToanTamUng>;
  deletePhieuThanhToanTamUng: (uuid: string) => Promise<void>;
  refreshPhieuThanhToanTamUngs: () => Promise<void>;
  getPhieuThanhToanTamUngByCustomer: (customerId: string) => Promise<PhieuThanhToanTamUng[]>;
  getPhieuThanhToanTamUngDetail: (uuid: string) => Promise<[]>;
}

interface PhieuThanhToanTamUngListResponse {
  results: PhieuThanhToanTamUng[];
  count: number;
}

export const fetchAllPhieuThanhToanTamUng = async (
  entitySlug: string,
  pageSize = 200
): Promise<PhieuThanhToanTamUng[]> => {
  let page = 1;
  let all: PhieuThanhToanTamUng[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<PhieuThanhToanTamUngListResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.PHIEU_THANH_TOAN_TAM_UNG}/`,
      {
        params: {
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};

/**
 * Hook for managing Advance Payment Settlement Voucher (PhieuThanhToanTamUng) data
 *
 * This hook provides functions to fetch, create, update, and delete advance payment settlement vouchers and their details.
 */
export const usePhieuThanhToanTamUng = (
  initialPhieuThanhToanTamUngs: PhieuThanhToanTamUng[] = []
): UsePhieuThanhToanTamUngReturn => {
  const [phieuThanhToanTamUngs, setPhieuThanhToanTamUngs] =
    useState<PhieuThanhToanTamUng[]>(initialPhieuThanhToanTamUngs);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchPhieuThanhToanTamUngs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<PhieuThanhToanTamUngResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_THANH_TOAN_TAM_UNG}/`
      );
      setPhieuThanhToanTamUngs(response.data.results);
    } catch (error) {
      console.error('Error fetching advance payment settlement vouchers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPhieuThanhToanTamUngByCustomer = async (customerId: string): Promise<PhieuThanhToanTamUng[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<PhieuThanhToanTamUngResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_THANH_TOAN_TAM_UNG}/?ma_kh=${customerId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching advance payment settlement vouchers by customer:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addPhieuThanhToanTamUng = async (
    newPhieuThanhToanTamUng: PhieuThanhToanTamUngInput
  ): Promise<PhieuThanhToanTamUng> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      // Send the complete data including nested details to backend
      // Backend will handle creating the main voucher and all related details
      const response = await api.post<PhieuThanhToanTamUng>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_THANH_TOAN_TAM_UNG}/`,
        newPhieuThanhToanTamUng
      );
      const addedPhieuThanhToanTamUng = response.data;

      setPhieuThanhToanTamUngs(prev => [...prev, addedPhieuThanhToanTamUng]);
      return addedPhieuThanhToanTamUng;
    } catch (error) {
      console.error('Error adding advance payment settlement voucher:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePhieuThanhToanTamUng = async (
    uuid: string,
    updatedPhieuThanhToanTamUng: PhieuThanhToanTamUngInput
  ): Promise<PhieuThanhToanTamUng> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<PhieuThanhToanTamUng>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_THANH_TOAN_TAM_UNG}/${uuid}/`,
        updatedPhieuThanhToanTamUng
      );

      const updatedData = response.data;
      setPhieuThanhToanTamUngs(prev => prev.map(item => (item.uuid === uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating advance payment settlement voucher:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePhieuThanhToanTamUng = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_THANH_TOAN_TAM_UNG}/${uuid}/`);
      setPhieuThanhToanTamUngs(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting advance payment settlement voucher:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getPhieuThanhToanTamUngDetail = async (uuid: string): Promise<[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<[]>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_THANH_TOAN_TAM_UNG}/${uuid}/chi-tiet/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching advance payment settlement voucher detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchPhieuThanhToanTamUngs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    phieuThanhToanTamUngs,
    isLoading,
    isLoadingDetail,
    addPhieuThanhToanTamUng,
    updatePhieuThanhToanTamUng,
    deletePhieuThanhToanTamUng,
    refreshPhieuThanhToanTamUngs: fetchPhieuThanhToanTamUngs,
    getPhieuThanhToanTamUngByCustomer,
    getPhieuThanhToanTamUngDetail
  };
};
